import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plugs, CheckCircle, XCircle, ArrowsClockwise, Lightning, MagnifyingGlass, Star, CloudArrowDown, CircleNotch } from "@phosphor-icons/react";

const PRIO = ["DISABLED", "LOW", "NORMAL", "HIGH", "CRITICAL"];
const PRIO_CLS = {
  CRITICAL: "bg-red-500/15 text-red-300 border-red-500/40",
  HIGH: "bg-accent/15 text-accent border-accent/40",
  NORMAL: "bg-surface-2 text-muted border-border",
  LOW: "bg-zinc-700/30 text-zinc-400 border-zinc-600/40",
  DISABLED: "bg-zinc-700/30 text-zinc-500 border-zinc-600/40",
};
const CAP_LABELS = {
  realtime_search: "Temps réel", historical_search: "Historique", ats_first_party: "ATS first-party",
  structured_search: "Recherche structurée", technographics: "Technographics",
  ats_jobs: "Jobs ATS", job_board_jobs: "Job boards", expiry_tracking: "Suivi expiration",
};

function ago(iso) {
  if (!iso) return "jamais";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `il y a ${m} min`;
  if (m < 2880) return `il y a ${Math.round(m / 60)} h`;
  return `il y a ${Math.round(m / 1440)} j`;
}

function ProviderCard({ p, onChanged }) {
  const [busy, setBusy] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [tq, setTq] = useState({ open: false, role: "Data Engineer", location: "Lille", results: null });

  const cfg = async (patch) => {
    setBusy("cfg");
    try {
      await api.post(`/admin/job-providers/${p.provider}/config`, patch);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const testConn = async () => {
    setBusy("test"); setTestRes(null);
    try {
      const { data } = await api.post(`/admin/job-providers/${p.provider}/test`);
      setTestRes(data);
      if (data.ok) toast.success(`${p.label} : connexion OK (${data.sample_count ?? 0} résultat(s) test)`);
      else toast.error(`${p.label} : ${data.error || "échec"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runTestQuery = async () => {
    setBusy("tq");
    try {
      const { data } = await api.post(`/admin/job-providers/${p.provider}/test-query`, { role: tq.role, location: tq.location });
      if (!data.ok) { toast.error(data.error || "Échec de la recherche de test"); setTq((s) => ({ ...s, results: [] })); }
      else setTq((s) => ({ ...s, results: data.results || [] }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const collect = async () => {
    setBusy("collect");
    try {
      await api.post(`/admin/job-providers/${p.provider}/collect`, {});
      toast.success(`Collecte ${p.label} lancée en arrière-plan — actualisez dans ~1 min.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const lr = p.last_run;
  const caps = Object.entries(p.capabilities || {}).filter(([k, v]) => CAP_LABELS[k] && v === true).map(([k]) => CAP_LABELS[k]);

  return (
    <div className="surface rounded-xl border border-border p-4 flex flex-col gap-3" data-testid={`job-provider-${p.provider}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-bold text-fg">{p.label}</h3>
          {p.france_it && <Star size={13} weight="fill" className="text-accent" title="France / IT prioritaire" />}
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${p.configured ? "text-emerald-400" : "text-red-400"}`}>
          {p.configured ? <CheckCircle size={13} weight="fill" /> : <XCircle size={13} weight="fill" />}
          {p.configured ? "Clé OK" : "Clé manquante"}
        </span>
      </div>

      {caps.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {caps.map((c) => <span key={c} className="text-[10px] bg-surface-2 border border-border rounded px-1.5 py-0.5 text-muted">{c}</span>)}
        </div>
      )}

      {/* Config */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => cfg({ enabled: !p.enabled })} disabled={busy === "cfg"} data-testid={`provider-enabled-${p.provider}`}
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${p.enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-surface-2 text-muted border-border"}`}>
          {p.enabled ? "Activé" : "Désactivé"}
        </button>
        <Select value={p.priority} onValueChange={(v) => cfg({ priority: v })}>
          <SelectTrigger className={`h-7 w-[112px] text-[11px] font-semibold border rounded-md ${PRIO_CLS[p.priority] || PRIO_CLS.NORMAL}`} data-testid={`provider-priority-${p.provider}`}><SelectValue /></SelectTrigger>
          <SelectContent>{PRIO.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => cfg({ france_it: !p.france_it })} disabled={busy === "cfg"}
          className={`text-[11px] px-2.5 py-1 rounded-md border ${p.france_it ? "bg-accent/12 text-accent border-accent/40" : "bg-surface-2 text-muted border-border"}`}>
          France IT
        </button>
      </div>

      {/* Last run stats */}
      <div className="grid grid-cols-4 gap-1.5 text-center">
        <Stat label="Brut" value={lr?.raw_records ?? "—"} />
        <Stat label="Net-new" value={lr?.net_new ?? "—"} accent />
        <Stat label="Doublons" value={lr?.duplicates ?? "—"} />
        <Stat label="Crédits" value={lr?.credits_used ?? "—"} />
      </div>
      {lr && (lr.total_signals != null || lr.raw_records != null) && (
        <div className="rounded-md bg-accent/10 border border-accent/30 px-2.5 py-1.5 flex items-center gap-2" data-testid={`provider-total-${p.provider}`}>
          <CloudArrowDown size={14} weight="bold" className="text-accent shrink-0" />
          <span className="text-[12px] text-fg">
            <strong className="text-accent">{lr.total_signals ?? lr.raw_records}</strong> signaux récupérés au dernier run
            {lr.requests != null ? <span className="text-muted"> · {lr.requests} requête(s)</span> : null}
            {lr.pages_max != null ? <span className="text-muted"> · {lr.pages_max} pages max</span> : null}
          </span>
        </div>
      )}
      <div className="text-[11px] text-muted flex items-center justify-between">
        <span>Dernier run : {ago(lr?.completed_at || lr?.started_at)}{lr?.status === "error" ? " ⚠️" : ""}</span>
        <span>Cumul net-new : <strong className="text-fg">{p.totals?.net_new ?? 0}</strong></span>
      </div>
      <div className="text-[11px] text-muted flex items-center justify-between">
        <span data-testid={`provider-window-${p.provider}`}>Fenêtre incrémentale : <span className="text-accent/90">{p.state?.last_window || (lr?.window) || "1er run"}</span></span>
        {p.state?.circuit_open && <span className="text-red-400 font-semibold">⛔ circuit ouvert ({p.state.consecutive_errors} err.)</span>}
      </div>
      {lr?.status === "error" && lr?.error && <div className="text-[11px] text-red-400">{lr.error}</div>}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
        <button onClick={testConn} disabled={!p.configured || busy === "test"} data-testid={`provider-test-${p.provider}`}
          className="flex items-center gap-1.5 text-[11px] font-semibold border border-border rounded-md px-2.5 py-1.5 text-fg hover:border-accent/40 disabled:opacity-40">
          {busy === "test" ? <CircleNotch size={12} className="animate-spin" /> : <Plugs size={12} />} Tester
        </button>
        <button onClick={() => setTq((s) => ({ ...s, open: !s.open }))} disabled={!p.configured} data-testid={`provider-testquery-${p.provider}`}
          className="flex items-center gap-1.5 text-[11px] font-semibold border border-border rounded-md px-2.5 py-1.5 text-fg hover:border-accent/40 disabled:opacity-40">
          <MagnifyingGlass size={12} /> Recherche test
        </button>
        <button onClick={collect} disabled={!p.configured || !p.enabled || busy === "collect"} data-testid={`provider-collect-${p.provider}`}
          className="flex items-center gap-1.5 text-[11px] font-semibold bg-accent text-black rounded-md px-2.5 py-1.5 hover-lift disabled:opacity-40">
          {busy === "collect" ? <CircleNotch size={12} className="animate-spin" /> : <CloudArrowDown size={12} weight="bold" />} Collecter
        </button>
      </div>

      {testRes && !testRes.ok && <div className="text-[11px] text-red-400">Test : {testRes.error}</div>}

      {/* Test query panel */}
      {tq.open && (
        <div className="mt-1 p-3 rounded-lg bg-surface-2/50 border border-border space-y-2" data-testid={`testquery-panel-${p.provider}`}>
          <div className="flex flex-wrap gap-2">
            <input value={tq.role} onChange={(e) => setTq((s) => ({ ...s, role: e.target.value }))} placeholder="Métier"
              className="flex-1 min-w-[120px] bg-surface border border-border rounded px-2 py-1 text-xs text-fg" data-testid={`tq-role-${p.provider}`} />
            <input value={tq.location} onChange={(e) => setTq((s) => ({ ...s, location: e.target.value }))} placeholder="Ville"
              className="w-28 bg-surface border border-border rounded px-2 py-1 text-xs text-fg" data-testid={`tq-loc-${p.provider}`} />
            <button onClick={runTestQuery} disabled={busy === "tq"} data-testid={`tq-run-${p.provider}`}
              className="flex items-center gap-1 text-xs font-semibold border border-accent/40 text-accent rounded px-2.5 py-1">
              {busy === "tq" ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} />} Lancer
            </button>
          </div>
          <p className="text-[10px] text-muted">Aperçu non enregistré — juste pour juger la qualité de la source.</p>
          {tq.results && (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {tq.results.length === 0 && <div className="text-xs text-muted">Aucun résultat.</div>}
              {tq.results.map((r, i) => (
                <div key={i} className="text-[11px] border-b border-border/40 pb-1.5" data-testid={`tq-result-${p.provider}-${i}`}>
                  <div className="text-fg font-medium truncate">{r.role_title} — <span className="text-muted">{r.company_name}</span></div>
                  <div className="text-muted flex flex-wrap gap-x-3">
                    <span>📍 {r.city || r.country || "—"}</span>
                    {r.via && <span>via {r.via}</span>}
                    {r.distribution_source && <span className="text-accent/80">{r.distribution_source}</span>}
                    {r.website && <span>🌐 {r.website}</span>}
                    {r.posted_at && <span>{r.posted_at}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md bg-surface-2/60 border border-border py-1.5">
      <div className={`font-mono font-bold text-sm ${accent ? "text-accent" : "text-fg"}`}>{value}</div>
      <div className="text-[9px] text-muted uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function JobProvidersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/job-providers");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="surface rounded-xl p-5 mb-6 border border-border" data-testid="job-providers-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Lightning size={20} weight="duotone" className="text-accent" />
          <h2 className="font-display text-lg font-bold">Nouveaux providers Jobs</h2>
        </div>
        <button onClick={load} data-testid="job-providers-refresh" className="flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-3 py-1.5">
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>
      <p className="text-xs text-muted mb-4 max-w-3xl">JSearch (temps réel via Google Jobs), TheirStack (base structurée + technographics) et Fantastic.jobs (ATS first-party). Ils <strong className="text-fg">alimentent le même moteur canonical</strong> (dédup, résolution entreprise/bureau, taxonomie IT). KPI clé : <strong className="text-accent">NET-NEW signaux IT uniques</strong>.</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => <ProviderCard key={p.provider} p={p} onChanged={load} />)}
      </div>
    </div>
  );
}
